import EPageHeader from '@/components/everridge/EPageHeader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import UsersPageColumns from '@/features/superAdmin/features/users/components/UsersPageColumns';
import { useUsersListApi } from '@/features/superAdmin/features/users/hooks/useUsersListApi';
import { useUsersFilter } from '@/features/superAdmin/features/users/hooks/useUsersFilter';
import ESearchInput from '@/components/everridge/ESearchInput';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import UserManage from '@/features/superAdmin/features/users/components/UserManage';
import { useTenantsSelectOptionsApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsSelectOptionsApi';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import { Button } from '@/components/ui/button';
import SendIcon from '@/components/icons/SendIcon';
import CancelCircleIcon from '@/components/icons/CancelCircleIcon';
import AddIcon from '@/components/icons/AddIcon';
import EDropDownMenu from '@/components/everridge/EDropDownMenu';
import MoreVerticalIcon from '@/components/icons/MoreVerticalIcon';
import DownloadIcon from '@/components/icons/DownloadIcon';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

const SuperAdminUsersPage = () => {
	const [userId, setUserId] = useState('');
	const [showUserDetails, setShowUserDetails] = useState(false);
	const [page, setPage] = useState(0);
	const [searchText, setSearchText] = useState('');
	const [isExporting, setIsExporting] = useState(false);

	const { t } = useTranslation();
	const axiosInstance = useAxiosAuth();
	const columns = UsersPageColumns();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();

	const { usersListData } = useUsersListApi({ searchText });

	const { filter, setFilter } = useUsersFilter();
	const { tenantsSelectData, isLoadingTenantsSelect } =
		useTenantsSelectOptionsApi();

	const tenantOptions = useMemo(
		() => tenantsSelectData?.items ?? [],
		[tenantsSelectData?.items],
	);
	const allTenantsLabel = t(
		'main.superAdmin.users.form.tenantFilter.allTenants',
	);
	const tenantSelectOptions = useMemo(
		() =>
			tenantOptions.map((tenant) => ({
				value: tenant.id,
				label: tenant.name ?? tenant.id,
			})),
		[tenantOptions],
	);

	useEffect(() => {
		if (isLoadingTenantsSelect || !filter.tenantId) return;
		const isStillValid = tenantOptions.some(
			(tenant) => tenant.id === filter.tenantId,
		);
		if (!isStillValid) {
			setFilter({ tenantId: null, skip: 0 });
			setPage(0);
		}
	}, [
		filter.tenantId,
		isLoadingTenantsSelect,
		setFilter,
		tenantOptions,
	]);

	if (userRole !== Roles.superAdmin) return null;

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setFilter({
			skip: filter.take * newPage,
		});
	};

	const handleSearchText = (text: string) => {
		setPage(0);
		setFilter({ skip: 0 });
		setSearchText(text);
	};

	const handleTenantFilterChange = (value: string | null) => {
		setPage(0);
		setFilter({
			tenantId: value,
			skip: 0,
		});
	};

	const handleShowDetails = (id: string) => {
		setUserId(id);
		setShowUserDetails(true);
	};

	const handleCreateUser = () => {
		setUserId('');
		setShowUserDetails(true);
	};

	const handleExportExcel = useCallback(async () => {
		setIsExporting(true);
		try {
			const url = UrlHelper.buildUrlWithQueryParams(
				ApiConfig.queries.superAdmin.users.endpoints.export,
				{
					...(searchText ? { searchText } : {}),
					...(filter.tenantId ? { tenantId: filter.tenantId } : {}),
				},
			);
			const { data } = await axiosInstance.get<Blob>(url, {
				responseType: 'blob',
			});
			const href = URL.createObjectURL(data);
			const a = document.createElement('a');
			a.href = href;
			a.download = `users-${new Date().toISOString().slice(0, 10)}.xlsx`;
			a.click();
			URL.revokeObjectURL(href);
		} finally {
			setIsExporting(false);
		}
	}, [axiosInstance, filter.tenantId, searchText]);

	return (
		<section
			className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
			<EPageHeader
				title={t('main.superAdmin.users.title')}
				search={
					<div
						className={cn(
							'flex w-full flex-col gap-2',
							!isMobile && 'w-auto flex-row flex-wrap items-center gap-3',
						)}>
						<ESearchInput
							value={searchText}
							setValue={handleSearchText}
							placeholder={t(
								'main.superAdmin.users.form.search.placeholder',
							)}
							containerClassName={cn(!isMobile && 'w-60')}
						/>
						<div
							className={cn(
								'flex w-full items-center gap-2',
								!isMobile && 'w-auto',
							)}>
							<span className='shrink-0 text-sm font-medium whitespace-nowrap text-popover-800'>
								{t('main.superAdmin.users.form.tenantFilter.title')}
							</span>
							<ESingleSelectPicker
								value={filter.tenantId}
								onChange={handleTenantFilterChange}
								options={tenantSelectOptions}
								allOptionLabel={allTenantsLabel}
								placeholder={t(
									'main.superAdmin.users.form.tenantFilter.placeholder',
								)}
								disabled={isLoadingTenantsSelect}
								triggerClassName={cn(!isMobile && 'w-56')}
							/>
						</div>
					</div>
				}>
				<Button
					variant='outline'
					size={isMobile ? 'icon' : 'default'}
					disabled={isExporting}
					onClick={() => void handleExportExcel()}>
					<DownloadIcon />
					{!isMobile && t('main.superAdmin.users.exportExcel')}
				</Button>
				<EDropDownMenu
					label={t('elements.buttons.more')}
					icon={MoreVerticalIcon}
					containerClassName='w-auto'>
					<Button
						variant='ghost'
						className='justify-start rounded-none!'
						onClick={() => {}}>
						<SendIcon />
						{!isMobile && t('elements.buttons.sendInvitationAll')}
					</Button>
					<Button
						variant='ghost'
						className='justify-start rounded-none!'
						onClick={() => {}}>
						<CancelCircleIcon />
						{!isMobile && t('elements.buttons.reserPasswordAll')}
					</Button>
				</EDropDownMenu>
				<Button
					variant='outline'
					size={isMobile ? 'icon' : 'default'}
					onClick={handleCreateUser}>
					<AddIcon />
					{!isMobile && t('elements.buttons.addUser')}
				</Button>
			</EPageHeader>
			<div
				className={cn(
					'h-full flex flex-col gap-6',
					isMobile && 'gap-4',
				)}>
				<ETableWithApiPagination
					columns={columns}
					data={usersListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowDetails}
				/>
			</div>
			{showUserDetails && (
				<UserManage
					id={userId}
					onClose={setShowUserDetails}
				/>
			)}
		</section>
	);
};

export default SuperAdminUsersPage;
